import React from "react";
import { Customer } from "../../../../types/inventory/CustomerTypes";
import { customerDisplayName } from "../../../../types/inventory/customerRecord";
import { dayjs } from "../../../../utils/time";
import { validateFullDateOfBirth } from "../../../../utils/healthcare/ValidateDateOfBirth";

/**
 * CU-86d44j2ac — the information-strip presentation with customer-safe content only: optional
 * demographics appear only when populated, and nothing clinical is shown.
 */

interface InfoCol {
	key: string;
	label: string;
	value: React.ReactNode;
	wrap?: boolean;
}

// CU-86d44j2ac: a date of birth is a calendar day, validated and parsed as one — never read as a UTC instant.
const calendarDateOfBirth = (value: string | Date | null | undefined) => {
	const validationValue = value instanceof Date ? dayjs(value).format("YYYY-MM-DD") : value ?? null;
	const validation = validateFullDateOfBirth(validationValue);
	if (!validation.isValid || !validation.value) return null;
	const day = dayjs(validation.value);
	return day.isValid() ? day : null;
};

// CU-86d44j2ac: append a unit only to a bare number, so a stored unit is not doubled up.
const withUnit = (value: string, unit: string) =>
	/[a-zA-Z]/.test(value) ? value : `${value} ${unit}`;

const CustomerInfoStrip: React.FC<{ customer: Customer }> = ({ customer }) => {
	const cols: InfoCol[] = [];

	cols.push({ key: "name", label: "Customer", value: customerDisplayName(customer) });

	const emailVal = customer.email?.trim();
	const phoneVal = customer.phone?.trim();
	if (emailVal || phoneVal) {
		cols.push({
			key: "contact",
			label: "Contact",
			wrap: true,
			value: (
				<span style={{ display: "flex", flexDirection: "column", gap: 1 }}>
					{emailVal && (
						<span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							{emailVal}
						</span>
					)}
					{phoneVal && <span>{phoneVal}</span>}
				</span>
			),
		});
	}

	const dobDay = calendarDateOfBirth(customer.date_of_birth);
	const age = dobDay ? dayjs().diff(dobDay, "year") : null;
	const dobStr = dobDay ? dobDay.format("DD/MM/YYYY") : null;
	if (age !== null && dobStr) cols.push({ key: "age", label: "Age / DOB", value: `${age}y (${dobStr})` });
	else if (dobStr) cols.push({ key: "dob", label: "DOB", value: dobStr });

	const sexVal = customer.sex?.trim();
	const genderVal = customer.gender?.trim();
	if (sexVal && genderVal && sexVal.toLowerCase() !== genderVal.toLowerCase()) {
		cols.push({ key: "sex", label: "Sex / Gender", value: `${sexVal} / ${genderVal}` });
	} else if (sexVal || genderVal) {
		cols.push({ key: "sex", label: "Sex / Gender", value: sexVal || genderVal });
	}

	const ht = customer.height?.trim();
	const wt = customer.weight?.trim();
	if (ht && wt) cols.push({ key: "htwt", label: "Ht / Wt", value: `${withUnit(ht, "cm")} / ${withUnit(wt, "kg")}` });
	else if (ht) cols.push({ key: "height", label: "Height", value: withUnit(ht, "cm") });
	else if (wt) cols.push({ key: "weight", label: "Weight", value: withUnit(wt, "kg") });

	return (
		<div style={{ display: "flex", alignItems: "stretch", width: "100%", paddingTop: 6 }}>
			{cols.map((col, i) => (
				<React.Fragment key={col.key}>
					{i > 0 && (
						<div style={{ width: 1, background: "var(--mg-border, rgba(0,0,0,0.08))", margin: "0 10px", flexShrink: 0 }} />
					)}
					<div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
						<span style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.6px", color: "var(--mg-text-secondary)", lineHeight: 1, marginBottom: 3, display: "block", whiteSpace: "nowrap" }}>
							{col.label}
						</span>
						<span style={{ fontSize: 11, lineHeight: 1.3, overflow: "hidden", textOverflow: col.wrap ? undefined : "ellipsis", whiteSpace: col.wrap ? undefined : "nowrap", color: "var(--mg-text)", fontWeight: 500 }}>
							{col.value}
						</span>
					</div>
				</React.Fragment>
			))}
		</div>
	);
};

export default CustomerInfoStrip;
